import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, User, Calendar, Building2, AlertTriangle, StickyNote, Image as ImageIcon, X, FileDown, Loader2, Pencil, FilePlus, Trash2, ArrowLeftRight } from "lucide-react";
import { inspectionsApi, quotesApi, buildingsApi, doorsApi } from "@/services/api";
import { generateDoorReport } from "@/pdf/webReports";
import { toast } from "sonner";
import { useAsync } from "@/hooks/useAsync";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, statusTone, prettyStatus, apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

function Lightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose} data-testid="lightbox">
      <button className="absolute right-4 top-4 text-white" onClick={onClose}><X className="h-7 w-7" /></button>
      <img src={src} alt="Evidence" className="max-h-[90vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

function PhotoThumb({ src, onOpen }) {
  if (!src) return null;
  return (
    <button
      onClick={() => onOpen(src)}
      className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200"
      data-testid="inspection-photo-thumb"
    >
      <img src={src} alt="Evidence" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
    </button>
  );
}

function MoveInspectionDialog({ open, onOpenChange, inspection, onMoved }) {
  const [buildingId, setBuildingId] = useState("");
  const [doorId, setDoorId] = useState("");
  const [buildings, setBuildings] = useState([]);
  const [doors, setDoors] = useState([]);
  const [loadingDoors, setLoadingDoors] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBuildingId(inspection?.building_id || "");
    setDoorId("");
    buildingsApi.getAll()
      .then((r) => setBuildings(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setBuildings([]));
  }, [open, inspection?.building_id]);

  useEffect(() => {
    if (!buildingId) { setDoors([]); return; }
    setLoadingDoors(true);
    doorsApi.getAll(buildingId, { includePhotos: false })
      .then((r) => setDoors(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setDoors([]))
      .finally(() => setLoadingDoors(false));
  }, [buildingId]);

  const submit = async () => {
    if (!doorId) { toast.error("Pick the correct door"); return; }
    if (doorId === inspection?.door_id) { toast.error("That's the door this inspection is already on"); return; }
    setSaving(true);
    try {
      const res = await inspectionsApi.move(inspection.id, doorId);
      toast.success(`Moved to ${res.data?.to_door_reference || "the selected door"}`);
      onOpenChange(false);
      onMoved?.(res.data);
    } catch (e) { toast.error(apiError(e, "Could not move inspection")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="move-inspection-dialog">
        <DialogHeader>
          <DialogTitle>Move inspection to another door</DialogTitle>
          <DialogDescription>
            Use this when the inspection was recorded against the wrong door. It will be
            reassigned, and the status of both doors will update automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Building</label>
            <Select value={buildingId} onValueChange={(v) => { setBuildingId(v); setDoorId(""); }}>
              <SelectTrigger data-testid="move-building-select"><SelectValue placeholder="Select building" /></SelectTrigger>
              <SelectContent>
                {buildings.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Correct door</label>
            <Select value={doorId} onValueChange={setDoorId} disabled={!buildingId || loadingDoors}>
              <SelectTrigger data-testid="move-door-select">
                <SelectValue placeholder={loadingDoors ? "Loading doors…" : "Select the correct door"} />
              </SelectTrigger>
              <SelectContent>
                {doors.map((d) => (
                  <SelectItem key={d.id} value={d.id} disabled={d.id === inspection?.door_id}>
                    {d.door_reference}{d.location ? ` · ${d.location}` : ""}{d.id === inspection?.door_id ? " (current)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="move-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !doorId} data-testid="move-confirm-btn">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ArrowLeftRight className="mr-1.5 h-4 w-4" />} Move inspection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InspectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!(user?.is_company_admin || user?.is_platform_admin);
  const { data: insp, loading, error, reload } = useAsync(() => inspectionsApi.getOne(id), [id]);
  const [zoom, setZoom] = useState(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    if (!window.confirm("Archive this inspection report? It will be hidden from lists, reports and the door's history but kept for audit — an admin can restore it later.")) return;
    setDeleting(true);
    try {
      await inspectionsApi.remove(insp.id);
      toast.success("Inspection archived");
      navigate(-1);
    } catch (e) { toast.error(apiError(e, "Could not archive inspection")); }
    finally { setDeleting(false); }
  };

  const failed = insp?.failed_component_photos || [];
  const photos = insp?.photos || [];
  const [pdfBusy, setPdfBusy] = useState(false);
  const [quoteBusy, setQuoteBusy] = useState(false);

  const doDownloadReport = async () => {
    if (!insp?.door_id || !insp?.building_id) { toast.error("Missing door/building on this inspection"); return; }
    setPdfBusy(true);
    try { await generateDoorReport(insp.door_id, insp.building_id); toast.success("Report generated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate report"); }
    finally { setPdfBusy(false); }
  };

  const doCreateQuote = async () => {
    setQuoteBusy(true);
    try {
      const res = await quotesApi.autoFromInspection(insp.id);
      toast.success("Quote created");
      navigate(`/quotes/${res.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create quote — is this inspection a fail?"); }
    finally { setQuoteBusy(false); }
  };

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {loading && <LoadingState label="Loading inspection…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && insp && (
        <>
          <PageHeader
            title="Inspection report"
            subtitle={insp.door_reference ? `${insp.door_reference}${insp.building_name ? ` · ${insp.building_name}` : ""}` : insp.building_name}
            actions={
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={statusTone(insp.status)}>{prettyStatus(insp.status)}</Badge>
                <Button variant="outline" size="sm" onClick={() => navigate(`/inspections/${insp.id}/edit`)} data-testid="inspection-amend-btn">
                  <Pencil className="mr-1.5 h-4 w-4" /> Amend
                </Button>
                {(String(insp.status).toLowerCase() === "fail" || String(insp.status).toLowerCase() === "review") && (
                  <Button variant="outline" size="sm" disabled={quoteBusy} onClick={doCreateQuote} data-testid="inspection-create-quote">
                    {quoteBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FilePlus className="mr-1.5 h-4 w-4" />} Create quote
                  </Button>
                )}
                <Button variant="outline" size="sm" disabled={pdfBusy} onClick={doDownloadReport} data-testid="inspection-download-report">
                  {pdfBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} Report
                </Button>
                {isAdmin && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setMoveOpen(true)} data-testid="inspection-move-btn">
                      <ArrowLeftRight className="mr-1.5 h-4 w-4" /> Move door
                    </Button>
                    <Button variant="outline" size="sm" disabled={deleting} onClick={doDelete} data-testid="inspection-delete-btn" className="text-rose-600 hover:text-rose-700">
                      {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />} Delete
                    </Button>
                  </>
                )}
              </div>
            }
            testId="inspection-detail-header"
          />

          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card className="flex items-center gap-3 p-4">
              <Calendar className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-400">Inspected</p>
                <p className="text-sm font-medium text-slate-700">{fmtDateTime(insp.inspection_date)}</p>
              </div>
            </Card>
            <Card className="flex items-center gap-3 p-4">
              <User className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-400">Inspector</p>
                <p className="text-sm font-medium text-slate-700">{insp.inspector_name || "—"}</p>
              </div>
            </Card>
            {insp.building_id && (
              <Link to={`/buildings/${insp.building_id}`}>
                <Card className="flex items-center gap-3 p-4 transition-shadow hover:shadow-md">
                  <Building2 className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-400">Building</p>
                    <p className="text-sm font-medium text-slate-700">{insp.building_name || "View building"}</p>
                  </div>
                </Card>
              </Link>
            )}
          </div>

          {failed.length > 0 && (
            <Card className="mb-6 border-red-100 p-5">
              <div className="mb-3 flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <h3 className="font-semibold">Failed components ({failed.length})</h3>
              </div>
              <div className="space-y-4">
                {failed.map((f, idx) => {
                  const fp = (Array.isArray(f.photos) && f.photos.length
                    ? f.photos
                    : (f.photo ? [f.photo] : [])).filter(Boolean);
                  return (
                    <div key={idx} className="rounded-xl border border-slate-100 bg-slate-50 p-4" data-testid={`failed-component-${idx}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-800">{f.question || f.key || "Component"}</p>
                        {f.severity && <Badge variant="outline" className={statusTone(f.severity === "high" ? "fail" : f.severity === "low" ? "pass" : "review")}>{prettyStatus(f.severity)}</Badge>}
                        {f.leaf && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">{f.leaf}</span>}
                      </div>
                      {f.notes && <p className="mt-1 text-sm text-slate-600">{f.notes}</p>}
                      {fp.length > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
                          {fp.map((p, i) => <PhotoThumb key={i} src={p} onOpen={setZoom} />)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {insp.notes && (
            <Card className="mb-6 p-5">
              <div className="mb-2 flex items-center gap-2 text-slate-800">
                <StickyNote className="h-5 w-5 text-slate-400" />
                <h3 className="font-semibold">Inspector notes</h3>
              </div>
              <p className="whitespace-pre-line text-sm text-slate-600">{insp.notes}</p>
            </Card>
          )}

          {(photos.length > 0 || insp.label_photo) && (
            <Card className="mb-6 p-5">
              <div className="mb-3 flex items-center gap-2 text-slate-800">
                <ImageIcon className="h-5 w-5 text-slate-400" />
                <h3 className="font-semibold">Photos</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {insp.label_photo && <PhotoThumb src={insp.label_photo} onOpen={setZoom} />}
                {photos.map((p, i) => <PhotoThumb key={i} src={p} onOpen={setZoom} />)}
              </div>
            </Card>
          )}

          {insp.signature && (
            <Card className="p-5">
              <h3 className="mb-3 font-semibold text-slate-800">Signature</h3>
              {String(insp.signature).startsWith("data:image") ? (
                <img src={insp.signature} alt="Signature" className="max-h-32 rounded-lg border border-slate-200 bg-white p-2" />
              ) : (
                <p className="text-2xl italic text-slate-800" data-testid="signature-name">{insp.signature}</p>
              )}
            </Card>
          )}
        </>
      )}

      <Lightbox src={zoom} onClose={() => setZoom(null)} />
      {insp && (
        <MoveInspectionDialog
          open={moveOpen}
          onOpenChange={setMoveOpen}
          inspection={insp}
          onMoved={(data) => { if (data?.to_door_id) navigate(`/doors/${data.to_door_id}`); else reload(); }}
        />
      )}
    </div>
  );
}
